import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { auditLogKeys } from '@/features/audit-log';
import { getErrorMessage } from '@/shared/api/api-error';

import {
  addComplaintComment,
  assignComplaint,
  getComplaint,
  getComplaintAssignees,
  getComplaints,
  getUnreadComplaintMessages,
  resolveComplaint,
  setComplaintStatus,
} from './complaints.api';
import type {
  ComplaintListParams,
  ComplaintResolution,
  ComplaintStatus,
} from '../types/complaint.types';

export const complaintKeys = {
  all: ['complaints'] as const,
  list: (params: ComplaintListParams) => [...complaintKeys.all, 'list', params] as const,
  detail: (complaintId: number) => [...complaintKeys.all, 'detail', complaintId] as const,
  unread: (countryId: number | undefined) => [...complaintKeys.all, 'unread', countryId] as const,
};

/** An open conversation checks for new messages this often (only while the tab is visible). */
const CONVERSATION_REFRESH_MS = 5_000;
/** Messages checks for conversations waiting on you this often (only while the tab is visible). */
const MESSAGES_REFRESH_MS = 10_000;

export function useComplaints(params: ComplaintListParams) {
  return useQuery({
    queryKey: complaintKeys.list(params),
    queryFn: ({ signal }) => getComplaints(params, signal),
    placeholderData: (previous) => previous,
  });
}

/**
 * One complaint. `live` keeps an open conversation current, like a chat:
 * new customer messages appear without a reload. Opening it also reads it, so
 * Messages is refreshed with it.
 */
export function useComplaint(complaintId: number | undefined, { live = false } = {}) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: complaintKeys.detail(complaintId ?? 0),
    queryFn: async ({ signal }) => {
      const complaint = await getComplaint(complaintId as number, signal);
      void queryClient.invalidateQueries({ queryKey: [...complaintKeys.all, 'unread'] });
      return complaint;
    },
    enabled: complaintId !== undefined,
    refetchInterval: live ? CONVERSATION_REFRESH_MS : false,
  });
}

/** Conversations where the customer wrote last and you have not read it (complaints.manage). */
export function useUnreadComplaintMessages(countryId: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: complaintKeys.unread(countryId),
    queryFn: ({ signal }) => getUnreadComplaintMessages(countryId, signal),
    enabled,
    refetchInterval: MESSAGES_REFRESH_MS,
    refetchOnWindowFocus: true,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: complaintKeys.all });
    void queryClient.invalidateQueries({ queryKey: ['orders', 'detail'] });
    // Every complaint action is audited, so the item's History is stale too.
    void queryClient.invalidateQueries({ queryKey: auditLogKeys.all });
  };
}

export function useAddComplaintComment(complaintId: number) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (text: string) => addComplaintComment(complaintId, text),
    // No success toast: like a chat, the message appearing in the thread is the confirmation.
    onSuccess: () => invalidate(),
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not send the message.'));
    },
  });
}

export function useSetComplaintStatus(complaintId: number) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ status, reason }: { status: ComplaintStatus; reason: string | null }) =>
      setComplaintStatus(complaintId, status, reason),
    onSuccess: (changed, { status }) => {
      toast.success(changed === 0 ? 'Nothing to change.' : `Complaint marked ${status}.`);
      invalidate();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not change the complaint.'));
    },
  });
}

export function useComplaintAssignees(complaintId: number, enabled: boolean) {
  return useQuery({
    queryKey: [...complaintKeys.detail(complaintId), 'assignees'] as const,
    queryFn: ({ signal }) => getComplaintAssignees(complaintId, signal),
    enabled,
  });
}

export function useAssignComplaint(complaintId: number) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (assigneeUserId: number | null) => assignComplaint(complaintId, assigneeUserId),
    onSuccess: (_changed, assigneeUserId) => {
      toast.success(assigneeUserId === null ? 'Complaint unassigned.' : 'Complaint assigned.');
      invalidate();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not assign the complaint.'));
    },
  });
}

/** Errors (amount above what remains, a refund already waiting) are shown in the dialog. */
export function useResolveComplaint(complaintId: number) {
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { outcome: ComplaintResolution; amount: number | null; reason: string }) =>
      resolveComplaint(complaintId, input),
    onSuccess: (approvalId) => {
      toast.success(
        approvalId === null
          ? 'Complaint resolved with no refund.'
          : 'Refund requested. The complaint closes when another staff member approves it.',
      );
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['refunds'] });
    },
  });
}
